import React from 'react';
import { PaintBucket, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета краски
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Парсинг числовых значений
  const surfaceArea = parseFloat(formData.surfaceArea) || 0;
  const coatsNumber = parseInt(formData.coatsNumber) || 2;
  
  // Обработчик изменения площади поверхности
  const handleSurfaceAreaChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      surfaceArea: newValue
    }));
  };
  
  // Обработчик изменения количества слоев
  const handleCoatsNumberChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      coatsNumber: newValue
    }));
  };
  
  // Обработчик изменения типа краски
  const handlePaintTypeChange = (paintType) => {
    setFormData(prev => ({
      ...prev,
      paintType
    }));
  };
  
  // Обработчик изменения качества краски
  const handlePaintQualityChange = (paintQuality) => {
    setFormData(prev => ({
      ...prev,
      paintQuality
    }));
  };
  
  // Функция расчета
  const calculatePaint = () => {
    // Нажимаем на кнопку подтверждения формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предустановленные площади для быстрого выбора
  const predefinedAreas = [
    { value: '10', label: '10 м²' },
    { value: '20', label: '20 м²' },
    { value: '50', label: '50 м²' },
    { value: '100', label: '100 м²' }
  ];
  
  // Предустановленные типы краски для быстрого выбора
  const popularPaintTypes = [
    { id: 'water', name: 'Водоэмульсионная' },
    { id: 'acrylic', name: 'Акриловая' },
    { id: 'latex', name: 'Латексная' },
    { id: 'alkyd', name: 'Алкидная' }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый расчет
      </h3>
      
      <div className="space-y-5">
        {/* Площадь покрытия */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="surfaceAreaSlider" className="text-gray-600 dark:text-gray-400">Площадь покрытия</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(surfaceArea, 1)} м²</span>
          </div>
          <input
            type="range"
            id="surfaceAreaSlider"
            min="1"
            max="200"
            step="1"
            value={surfaceArea}
            onChange={handleSurfaceAreaChange}
            aria-label="Площадь покрытия"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            disabled={formData.calculationType === 'room'}
          />
          
          {/* Кнопки с предопределенными площадями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAreas.map(area => (
              <button
                key={area.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, surfaceArea: area.value }))}
                disabled={formData.calculationType === 'room'}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.surfaceArea === area.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  } ${formData.calculationType === 'room' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {area.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество слоев */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="coatsNumberSlider" className="text-gray-600 dark:text-gray-400">Количество слоев</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {coatsNumber} {coatsNumber === 1 ? 'слой' : coatsNumber < 5 ? 'слоя' : 'слоев'}
            </span>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(num => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, coatsNumber: num.toString() }))}
                className={`flex-1 py-2 text-sm rounded border transition-colors
                  ${parseInt(formData.coatsNumber) === num 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {num}
              </button>
            ))}
          </div>
          {parseInt(formData.coatsNumber) === 1 && (
            <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Рекомендуется минимум 2 слоя для лучшего покрытия
            </div>
          )}
        </div>
        
        {/* Популярные типы краски */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Популярные типы краски
          </div>
          <div className="grid grid-cols-2 gap-2">
            {popularPaintTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handlePaintTypeChange(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.paintType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор качества краски */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Качество краски
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'economy', name: 'Эконом' },
              { id: 'medium', name: 'Среднее' },
              { id: 'premium', name: 'Премиум' },
              { id: 'luxury', name: 'Люкс' }
            ].map(quality => (
              <button
                key={quality.id}
                type="button"
                onClick={() => handlePaintQualityChange(quality.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.paintQuality === quality.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {quality.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Включение/выключение потолка */}
        <div>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, ceilingIncluded: !prev.ceilingIncluded }))}
            className={`w-full p-3 text-sm rounded-lg border transition-colors
              ${formData.ceilingIncluded 
                ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            {formData.ceilingIncluded ? '✓ Потолок включен в расчет' : '☐ Включить потолок'}
          </button>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculatePaint}
          icon={<PaintBucket className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать расход краски
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;